import type { LucideIcon } from "lucide-react";
import {
  ArrowLeftRight,
  Banknote,
  BarChart3,
  BookOpen,
  Camera,
  CircleDollarSign,
  ClipboardList,
  FileSpreadsheet,
  FileText,
  FolderOpen,
  HandCoins,
  HardHat,
  Home,
  Info,
  Landmark,
  Layers,
  LayoutDashboard,
  Package,
  Receipt,
  ShoppingBag,
  ShoppingCart,
  TriangleAlert,
  Wallet,
  Wrench,
} from "lucide-react";

import type { SidebarNavContext, SidebarNavGroupItem, SidebarNavSection } from "@/navigation/types";
import type { Permission } from "@/types";

export type ProjectNavSection = {
  suffix: string;
  labelKey: string;
  icon: LucideIcon;
  permission: Permission;
};

export type ProjectNavGroup = {
  titleKey: string;
  icon: LucideIcon;
  items: ProjectNavSection[];
};

const TRACKING_NAV: ProjectNavSection = {
  suffix: "tracking",
  labelKey: "project.sections.tracking",
  icon: LayoutDashboard,
  permission: "project.tracking.view",
};

const OVERVIEW_NAV: ProjectNavSection = {
  suffix: "overview",
  labelKey: "project.sections.overview",
  icon: Info,
  permission: "project.overview.view",
};

const SERVICE_FRONTS_NAV: ProjectNavSection = {
  suffix: "project-service-fronts",
  labelKey: "project.sections.projectServiceFront",
  icon: Layers,
  permission: "project.projectServiceFront.view",
};

const DIARY_NAV: ProjectNavSection = {
  suffix: "project-diary-entries",
  labelKey: "project.sections.projectDiaryEntries",
  icon: BookOpen,
  permission: "project.projectDiary.view",
};

const OCCURRENCES_NAV: ProjectNavSection = {
  suffix: "occurrences",
  labelKey: "project.sections.occurrences",
  icon: TriangleAlert,
  permission: "project.occurrences.view",
};

const MATERIALS_NAV: ProjectNavSection = {
  suffix: "materials",
  labelKey: "project.sections.materials",
  icon: Package,
  permission: "project.materials.view",
};

const EQUIPMENTS_NAV: ProjectNavSection = {
  suffix: "equipments",
  labelKey: "project.sections.equipments",
  icon: Wrench,
  permission: "project.equipments.view",
};

const CLIENT_BUDGETS_NAV: ProjectNavSection = {
  suffix: "client-budgets",
  labelKey: "project.sections.clientBudgets",
  icon: Receipt,
  permission: "project.clientBudgets.view",
};

const BUDGETS_NAV: ProjectNavSection = {
  suffix: "project-budgets",
  labelKey: "project.sections.projectBudgets",
  icon: Wallet,
  permission: "project.budgets.view",
};

const PURCHASE_REQUESTS_NAV: ProjectNavSection = {
  suffix: "purchase-requests",
  labelKey: "project.sections.purchaseRequests",
  icon: ClipboardList,
  permission: "project.purchaseRequests.view",
};

const QUOTATIONS_NAV: ProjectNavSection = {
  suffix: "project-quotations",
  labelKey: "project.sections.projectQuotations",
  icon: FileSpreadsheet,
  permission: "project.quotations.view",
};

const PURCHASE_ORDERS_NAV: ProjectNavSection = {
  suffix: "purchase-orders",
  labelKey: "project.sections.purchaseOrders",
  icon: ShoppingCart,
  permission: "project.purchaseOrders.view",
};

const COSTS_NAV: ProjectNavSection = {
  suffix: "costs",
  labelKey: "project.sections.costs",
  icon: CircleDollarSign,
  permission: "project.finance.view",
};

const PAYABLES_NAV: ProjectNavSection = {
  suffix: "payables",
  labelKey: "project.sections.payables",
  icon: Banknote,
  permission: "finance.payables.view",
};

const RECEIVABLES_NAV: ProjectNavSection = {
  suffix: "receivables",
  labelKey: "project.sections.receivables",
  icon: HandCoins,
  permission: "finance.receivables.view",
};

const INVOICES_NAV: ProjectNavSection = {
  suffix: "invoices",
  labelKey: "project.sections.invoices",
  icon: Receipt,
  permission: "finance.fiscal.view",
};

const CASH_FLOW_NAV: ProjectNavSection = {
  suffix: "cash-flow",
  labelKey: "project.sections.cashFlow",
  icon: ArrowLeftRight,
  permission: "finance.cashFlow.view",
};

const DOCUMENTS_NAV: ProjectNavSection = {
  suffix: "documents",
  labelKey: "project.sections.documents",
  icon: FileText,
  permission: "project.documents.view",
};

const PHOTOS_NAV: ProjectNavSection = {
  suffix: "photos",
  labelKey: "project.sections.photos",
  icon: Camera,
  permission: "project.photos.view",
};

const REPORTS_NAV: ProjectNavSection = {
  suffix: "reports",
  labelKey: "project.sections.reports",
  icon: BarChart3,
  permission: "project.projectReport.view",
};

export const PROJECT_NAV_GROUPS: ProjectNavGroup[] = [
  { titleKey: "project.navGroups.overview", icon: Home, items: [TRACKING_NAV, OVERVIEW_NAV] },
  {
    titleKey: "project.navGroups.execution",
    icon: HardHat,
    items: [SERVICE_FRONTS_NAV, DIARY_NAV, OCCURRENCES_NAV, MATERIALS_NAV, EQUIPMENTS_NAV],
  },
  {
    titleKey: "project.navGroups.commercial",
    icon: Receipt,
    items: [CLIENT_BUDGETS_NAV],
  },
  {
    titleKey: "project.navGroups.purchases",
    icon: ShoppingBag,
    items: [BUDGETS_NAV, PURCHASE_REQUESTS_NAV, QUOTATIONS_NAV, PURCHASE_ORDERS_NAV, COSTS_NAV],
  },
  {
    titleKey: "project.navGroups.finance",
    icon: Landmark,
    items: [PAYABLES_NAV, RECEIVABLES_NAV, INVOICES_NAV, CASH_FLOW_NAV],
  },
  {
    titleKey: "project.navGroups.docs",
    icon: FolderOpen,
    items: [DOCUMENTS_NAV, PHOTOS_NAV, REPORTS_NAV],
  },
];

export const PROJECT_NAV_SECTIONS: ProjectNavSection[] = PROJECT_NAV_GROUPS.flatMap(
  (group) => group.items,
);

const PROJECT_BOTTOM_NAV_ORDER = [
  "tracking",
  "project-diary-entries",
  "project-service-fronts",
  "occurrences",
  "materials",
  "equipments",
  "overview",
  "client-budgets",
  "project-budgets",
  "purchase-requests",
  "project-quotations",
  "purchase-orders",
  "costs",
  "payables",
  "receivables",
  "invoices",
  "cash-flow",
  "documents",
  "photos",
  "reports",
] as const;

export function filterProjectNavSections(
  sections: ProjectNavSection[],
  can: (permission: Permission) => boolean,
): ProjectNavSection[] {
  return sections.filter((section) => can(section.permission));
}

function orderedProjectNavSections(can: (permission: Permission) => boolean): ProjectNavSection[] {
  const allowed = filterProjectNavSections(PROJECT_NAV_SECTIONS, can);
  const bySuffix = new Map(allowed.map((section) => [section.suffix, section]));
  const ordered: ProjectNavSection[] = [];
  for (const suffix of PROJECT_BOTTOM_NAV_ORDER) {
    const section = bySuffix.get(suffix);
    if (section) ordered.push(section);
  }
  return ordered;
}

/** First project section the user may open (sidebar order). Falls back to the project list. */
export function resolveDefaultProjectPath(
  projectId: string,
  can: (permission: Permission) => boolean,
): string {
  for (const group of PROJECT_NAV_GROUPS) {
    const [first] = filterProjectNavSections(group.items, can);
    if (first) return `/projects/${projectId}/${first.suffix}`;
  }
  return "/projects";
}

/** First four obra sections in execution-first order — mobile bottom bar. */
export function getProjectBottomNavPrimary(
  can: (permission: Permission) => boolean,
): ProjectNavSection[] {
  return orderedProjectNavSections(can).slice(0, 4);
}

/** Remaining obra sections — mobile “Mais” sheet, grouped like the sidebar. */
export function getProjectBottomNavMore(
  projectId: string,
  can: (permission: Permission) => boolean,
): SidebarNavGroupItem[] {
  const primarySuffixes = new Set(getProjectBottomNavPrimary(can).map((section) => section.suffix));

  return PROJECT_NAV_GROUPS.map((group) => ({
    kind: "group" as const,
    labelKey: group.titleKey,
    icon: group.icon,
    items: filterProjectNavSections(group.items, can)
      .filter((section) => !primarySuffixes.has(section.suffix))
      .map((section) => ({
        kind: "link" as const,
        to: `/projects/${projectId}/${section.suffix}`,
        end: false,
        labelKey: section.labelKey,
        icon: section.icon,
      })),
  })).filter((group) => group.items.length > 0);
}

export function resolveProjectSidebarNav({ params, can }: SidebarNavContext): SidebarNavSection[] {
  const projectId = params.projectId;
  if (!projectId) return [];

  const items = PROJECT_NAV_GROUPS.map((group) => ({
    kind: "group" as const,
    labelKey: group.titleKey,
    icon: group.icon,
    items: filterProjectNavSections(group.items, can).map(({ suffix, labelKey, icon }) => ({
      kind: "link" as const,
      to: `/projects/${projectId}/${suffix}`,
      end: false,
      labelKey,
      icon,
    })),
  })).filter((group) => group.items.length > 0);

  if (items.length === 0) return [];

  return [{ titleKey: "project.navGroups.overview", items }];
}
